<!-- ArtworkUpload.vue - Component for uploading artwork files with drag & drop -->
<template>
  <div class="artwork-upload">
    <!-- Dropzone Area -->
    <div 
      class="dropzone"
      :class="{ 
        'dropzone-active': isDragActive,
        'dropzone-has-file': selectedFile 
      }"
      @dragenter.prevent="handleDragEnter"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
      @click="handleDropzoneClick"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="acceptedFileTypes"
        @change="handleFileSelect"
        class="file-input"
      />

      <!-- No file selected -->
      <div v-if="!selectedFile" class="dropzone-content">
        <div class="dropzone-icon">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <rect x="2" y="2" width="36" height="36" rx="4" stroke="#0D72B1" stroke-width="2"/>
            <path d="M12 20L20 12L28 20" stroke="#0D72B1" stroke-width="2" stroke-linecap="round"/>
            <path d="M20 12V28" stroke="#0D72B1" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>

        <div class="dropzone-text">
          <span>Drop your image here, or </span>
          <button
            type="button"
            class="browse-link"
            @click.stop="openFileDialog"
          >browse</button>
        </div>

        <div class="dropzone-hint">
          Accepted file types: <span class="accepted-types">{{ acceptedTypesDisplay }}</span>
        </div>
      </div>

      <!-- File selected -->
      <div v-else class="selected-file">
        <div class="file-icon">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M6 4C6 2.89543 6.89543 2 8 2H20L26 8V28C26 29.1046 25.1046 30 24 30H8C6.89543 30 6 29.1046 6 28V4Z" fill="#0D72B1" fill-opacity="0.1"/>
            <path d="M20 2L26 8H20V2Z" fill="#0D72B1" fill-opacity="0.2"/>
          </svg>
        </div>
        <div class="file-details">
          <span class="file-name" :title="selectedFile.name">{{ selectedFile.name }}</span>
          <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
        </div>
        <button 
          type="button"
          class="remove-btn" 
          @click.stop="removeFile"
          title="Remove file"
        >
          ×
        </button>
      </div>
    </div>

    <!-- File Info -->
    <div class="file-info">
      <p class="file-size-limit">Maximum file size: {{ maxSizeMB }}MB</p>
    </div>

    <!-- Success Message with Image Preview -->
    <div v-if="uploadSuccess" class="upload-message success">
      <!-- Left: Image Preview -->
      <div class="image-preview">
        <img :src="imagePreviewUrl" alt="Uploaded image preview" />
      </div>
      
      <!-- Right: Success Message -->
      <div class="success-content">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="message-icon">
          <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#155724" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p>{{ uploadMessage }}</p>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="errors.length > 0" class="upload-message error">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="message-icon">
        <circle cx="8" cy="8" r="7" stroke="#721c24" stroke-width="2"/>
        <path d="M8 4V9" stroke="#721c24" stroke-width="2" stroke-linecap="round"/>
        <circle cx="8" cy="12" r="1" fill="#721c24"/>
      </svg>
      <div class="error-list">
        <p v-for="(error, index) in errors" :key="index">{{ error }}</p>
      </div>
    </div>

    <!-- Upload Progress -->
    <div v-if="uploading" class="upload-progress">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: uploadProgress + '%' }">
          <span class="progress-percentage">{{ uploadProgress }}%</span>
        </div>
      </div>
      <p class="progress-text">Uploading...</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ArtworkUpload',

  props: {
    /**
     * API endpoint for upload
     */
    uploadEndpoint: {
      type: String,
      default: 'api/upload/imprint'
    },

  /**
   * Existing uploaded image data (from Pinia store)
   * Expected shape: { filename?, file?, response?, urlFromStorage?, uploadedFileUrl?, ... }
   */
    initialImageData: {
      type: Object,
      default: null
    },
    
    /**
     * Maximum file size in MB
     */
    maxSizeMB: {
      type: Number,
      default: 24
    }
  },

  data() {
    return {
      selectedFile: null,
      isDragActive: false,
      uploadSuccess: false,
      uploadMessage: '',
      uploading: false,
      uploadProgress: 0,
      errors: [],
      acceptedFileTypes: '.ai,.bmp,.cdr,.eps,.jpeg,.jpg,.pdf,.png,.tiff',
      acceptedTypesDisplay: 'ai, bmp, cdr, eps, jpeg, jpg, pdf, png, tiff',
      uploadedFileUrl: null,
      imagePreviewUrl: null, // Preview URL for uploaded image
      dragCounter: 0 // Track nested drag events
    };
  },

  computed: {
    apiBaseUrl() {
      return window?.PXUTheme?.theme_settings?.api_beckend_url || 
             'https://dev.api-customapp.comda.com/';
    },
    
    maxSizeBytes() {
      return this.maxSizeMB * 1024 * 1024;
    }
  },

    watch: {
        initialImageData: {
            immediate: true,
            deep: true,
            handler(val) {
                // Reset UI when imageData is explicitly cleared (e.g. user clicks remove button)
                // Note: method switching no longer clears imageData (it is shared across methods)
                if (!val) {
                    this.selectedFile = null;
                    this.uploadSuccess = false;
                    this.uploadMessage = '';
                    this.uploadedFileUrl = null;
                    this.imagePreviewUrl = null;
                    this.errors = [];
                    return;
                }

                // Try to get URL from known fields (store may contain different shapes)
                const url =
                    val.urlFromStorage ||
                    val.uploadedFileUrl ||
                    val.response?.urlFromStorage ||
                    val.response?.url ||
                    val.url;

                if (!url) return;

                // Template relies on selectedFile existing (v-if="!selectedFile" / v-else)
                const name =
                    val.filename ||
                    val.file?.name ||
                    val.response?.result ||
                    'uploaded_image';

                const size = Number.isFinite(val.file?.size) ? val.file.size : 0;

                this.selectedFile = { name, size };
                this.uploadSuccess = true;
                this.uploadMessage = val.message || 'Image Uploaded Successfully!';
                this.uploadedFileUrl = url;
                this.imagePreviewUrl = url;
                this.errors = [];
                this.uploading = false;
                this.uploadProgress = 0;
            }
        }
    },

  methods: {
    // Click on dropzone area opens file dialog (only when no file is selected)
    handleDropzoneClick(e) {
      if (!this.selectedFile) {
        this.openFileDialog();
      }
    },

    // Drag & Drop handlers
    handleDragEnter(e) {
      this.dragCounter++;
      this.isDragActive = true;
    },

    handleDragOver(e) {
      this.isDragActive = true;
    },

    handleDragLeave(e) {
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.isDragActive = false;
      }
    },

    handleDrop(e) {
      this.dragCounter = 0;
      this.isDragActive = false;

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        this.processFile(files[0]);
      }
    },

    // File dialog
    openFileDialog() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },

    // File processing
    processFile(file) {
      this.errors = [];
      this.uploadSuccess = false;

      // Validate file
      if (!this.validateFile(file)) {
        return;
      }

      this.selectedFile = file;
      
      // Start upload
      this.uploadFile();
    },

    validateFile(file) {
      const allowedExtensions = ['.ai', '.bmp', '.cdr', '.eps', '.jpeg', '.jpg', '.png', '.pdf', '.tiff'];
      
      // Get file extension
      const fileName = file.name.toLowerCase();
      const fileExtension = '.' + fileName.split('.').pop();

      // Check file type
      if (!allowedExtensions.includes(fileExtension)) {
        this.errors.push(
          `Invalid file type. Only ${this.acceptedTypesDisplay} are allowed.`
        );
        return false;
      }

      // Check file size
      if (file.size > this.maxSizeBytes) {
        this.errors.push(
          `The file you are uploading is too large. The maximum file size is ${this.maxSizeMB}MB.`
        );
        return false;
      }

      return true;
    },

    async uploadFile() {
      if (!this.selectedFile) return;

      this.uploading = true;
      this.uploadProgress = 0;
      this.errors = [];

      // Extract file info
      const file = this.selectedFile;
      const file_type = file.type || ''; // MIME type
      const file_name = file.name || '';
      
      // Get file extension
      let file_name_type = '';
      const splitNameByDot = file_name.split('.');
      if (splitNameByDot.length > 0) {
        file_name_type = '.' + splitNameByDot[splitNameByDot.length - 1];
      }

      // Build FormData - match old working format
      const formData = new FormData();
      formData.append('file_type', file_type);
      formData.append('file_name', file_name);
      
      if (file_name_type) {
        formData.append('file_name_type', file_name_type);
      }
      
      formData.append('image', file); // Backend expects 'image', not 'file'
      formData.append('_method', 'PUT'); // Laravel method spoofing

      try {
        const response = await axios.post(
          `${this.apiBaseUrl}${this.uploadEndpoint}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                this.uploadProgress = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
              }
            }
          }
        );

        // Upload successful
        this.uploading = false;
        this.uploadSuccess = true;
        this.uploadMessage = 'Image Uploaded Successfully!';
        this.uploadedFileUrl = response.data.urlFromStorage || response.data.url || response.data.file_url;
        
        // Create preview URL from uploaded file
        // If image type (jpeg, jpg, png, gif), show preview from S3 URL
        const imageExtensions = ['.jpeg', '.jpg', '.png', '.gif', '.webp'];
        const fileName = this.selectedFile.name.toLowerCase();
        const isImageFile = imageExtensions.some(ext => fileName.endsWith(ext));
        
        if (isImageFile && this.uploadedFileUrl) {
          this.imagePreviewUrl = this.uploadedFileUrl;
        } else {
          // For non-image files, create local preview from File object
          this.imagePreviewUrl = URL.createObjectURL(this.selectedFile);
        }

        // Emit success event
        this.$emit('upload-success', {
          file: this.selectedFile,
          url: this.uploadedFileUrl,
          response: response.data
        });

        console.log('[ArtworkUpload] Upload successful:', response.data);

      } catch (error) {
        this.uploading = false;
        this.uploadSuccess = false;
        
        // Enhanced error logging
        console.error('[ArtworkUpload] Upload error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          headers: error.response?.headers,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers
          }
        });
        
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error ||
                           error.message ||
                           'Upload failed. Please try again.';
        
        this.errors.push(errorMessage);
        
        // Emit error event with full error object
        this.$emit('upload-error', {
          error,
          message: errorMessage,
          status: error.response?.status
        });
      }
    },

    removeFile() {
      // Revoke object URL if it was created
      if (this.imagePreviewUrl && this.imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.imagePreviewUrl);
      }
      
      this.selectedFile = null;
      this.uploadSuccess = false;
      this.uploadMessage = '';
      this.uploadedFileUrl = null;
      this.imagePreviewUrl = null;
      this.errors = [];
      this.uploadProgress = 0;
      
      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }

      // Emit remove event
      this.$emit('file-removed');
    },

    // Utility
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }
  },
  
  beforeUnmount() {
    // Cleanup: Revoke object URL if it exists
    if (this.imagePreviewUrl && this.imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.imagePreviewUrl);
    }
  }
};
</script>

<style scoped lang="scss">
@use '@/styles/utils' as *;

.artwork-upload {
  width: 100%;
}

// Dropzone
.dropzone {
  border: 2px dashed $color-border;
  border-radius: 8px;
  padding: 32px 24px;
  background: $color-bg-light;
  cursor: pointer; // Entire zone is clickable
  transition: all 0.2s ease;
  
  &:hover {
    border-color: $brand-primary;
    background: $brand-primary-lighter;

  }
  
  &.dropzone-active {
    border-color: $brand-primary;
    background: #E6F3FF;
    border-style: solid;
  }
  
  &.dropzone-has-file {
    padding: 16px;
    cursor: default;
    
    &:hover {
      border-color: $color-border;
      background: $color-bg-light;
    }
  }
}

.file-input {
  display: none;
}

// Empty state
.dropzone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.dropzone-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
  
  svg {
    display: block;
  }
}

.dropzone-text {
  font-size: 16px;
  color: $color-text;
  line-height: 22px;
}

.browse-link {
  background: none;
  border: none;
  padding: 0;
  color: $brand-primary;
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  
  &:hover {
    color: $brand-primary-dark;
  }
}

.dropzone-hint {
  font-size: 13px;
  color: $color-text-light;
  line-height: 18px;
  
  .accepted-types {
    font-weight: 600;
    color: $color-text-light;
  }
}

// File selected state
.selected-file {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: $color-white;
  border-radius: 6px;
  border: 1px solid $color-border-light;
}

.file-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    display: block;
  }
}

.file-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-name {
  font-size: 14px;
  font-weight: 600;
  color: $color-text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: 12px;
  color: $color-text-light;
}

.remove-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  background: #F5F5F5;
  color: #666666;
  border-radius: 4px;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: $color-danger;
    color: $color-white;
  }
}

// File Info
.file-info {
  margin-top: 12px;
  padding: 8px 0;
  
  .file-size-limit {
    font-size: 12px;
    color: $color-text-light;
    margin: 0;
    line-height: 16px;
  }
}

// Upload Messages
.upload-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 6px;
  margin-top: 12px;
  
  .message-icon {
    flex-shrink: 0;
    margin-top: 2px;
  }
  
  p {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
  }
  
  &.success {
    background: #D4EDDA;
    border: 1px solid #C3E6CB;
    color: #155724;
    
    // New layout with image preview
    .image-preview {
      flex-shrink: 0;
      width: 80px;
      height: 80px;
      border-radius: 4px;
      overflow: hidden;
      background: $color-white;
      border: 1px solid #C3E6CB;
      display: flex;
      align-items: center;
      justify-content: center;
      
      img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        display: block;
      }
    }
    
    .success-content {
      flex: 1;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      p {
        margin: 0;
        font-size: 14px;
        line-height: 20px;
        font-weight: 600;
      }
    }
  }
  
  &.error {
    background: #F8D7DA;
    border: 1px solid #F5C6CB;
    color: #721C24;
    
    .error-list {
      flex: 1;
      
      p {
        margin-bottom: 4px;
        
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

// Upload Progress
.upload-progress {
  margin-top: 12px;
}

.progress-bar {
  width: 100%;
  height: 32px;
  background: #E9ECEF;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, $brand-primary 0%, $brand-secondary 100%);
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
}

.progress-percentage {
  color: $color-white;
  font-size: 13px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.progress-text {
  text-align: center;
  font-size: 13px;
  color: $color-text-light;
  margin: 0;
  font-weight: 500;
}
</style>
